import { Link } from "react-router-dom";
import { Reveal, MaskedLine } from "@/components/Motion";
import { SEO } from "@/components/SEO";
import { getLegal } from "@/data/legal";
import { useLang } from "@/i18n";

export default function LegalPage({ docKey }) {
  const { lang } = useLang();
  const { docs, links } = getLegal(lang);
  const doc = docs[docKey];
  if (!doc) return null;
  return (
    <div data-testid={`legal-page-${docKey}`} className="bg-alabaster pb-24 pt-40">
      <SEO
        title={`${doc.title} | VCK Business Lab`}
        description={`${doc.title} for VCK Business Lab — virtual business support company based in Edmonton, Alberta, Canada.`}
        path={doc.path}
      />
      <div className="mx-auto max-w-3xl px-6 sm:px-8">
        <header className="text-center">
          <MaskedLine delay={0.1}>
            <span className="overline text-gold-muted">VCK Business Lab</span>
          </MaskedLine>
          <h1 className="mt-6 font-serif text-4xl font-light tracking-tight text-obsidian sm:text-5xl">
            <MaskedLine delay={0.25}>{doc.title}</MaskedLine>
          </h1>
          <MaskedLine delay={0.4}>
            <span
              data-testid={`legal-updated-${docKey}`}
              className="mt-4 block font-mono text-xs uppercase tracking-[0.25em] text-slate-400"
            >
              Last Updated: August 2026
            </span>
          </MaskedLine>
        </header>

        <div className="mt-14 space-y-10">
          {doc.sections.map((s, i) => (
            <Reveal key={s.heading} delay={Math.min(i * 0.05, 0.4)}>
              <section data-testid={`legal-section-${docKey}-${i}`}>
                <h2 className="font-serif text-xl text-obsidian">{s.heading}</h2>
                {s.paragraphs?.map((p) => (
                  <p key={p.slice(0, 40)} className="mt-3 text-sm leading-relaxed text-slate-600">
                    {p}
                  </p>
                ))}
                {s.bullets && (
                  <ul className="mt-3 list-disc space-y-2 ps-5 text-sm leading-relaxed text-slate-600">
                    {s.bullets.map((b) => (
                      <li key={b.slice(0, 40)}>{b}</li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 rounded-xl border border-obsidian/10 bg-white p-8">
            <p className="text-xs leading-relaxed text-slate-500">
              These website terms supplement, and do not replace, any signed Master Services
              Agreement, quote, service schedule, or Statement of Work between you and VCK Business
              Lab for individual engagements.
            </p>
            <nav className="mt-6 flex flex-wrap gap-x-5 gap-y-2" aria-label="Legal pages">
              {links
                .filter((l) => l.key !== docKey)
                .map((l) => (
                  <Link
                    key={l.key}
                    to={docs[l.key].path}
                    data-testid={`legal-xlink-${l.key}`}
                    className="text-xs text-slate-400 underline underline-offset-4 transition-colors hover:text-gold-muted"
                  >
                    {l.label}
                  </Link>
                ))}
            </nav>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
